import React from 'react'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/styles'
import { useHistory } from 'react-router-dom'

import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Button,
  Divider,
  List,
  ListItem,
  Typography,
  ListItemText
} from '@material-ui/core'
import ArrowRightIcon from '@material-ui/icons/ArrowRight'
import {
  formatPrice,
  formatToPercentage,
  safeDivision
} from '../../../utils/format'
import { useSelector } from 'react-redux'
import { UnavailableValue } from 'components/UnavailableValue'
import {
  FUTURE_SALES_MESSAGE,
  getDisabledFieldMessage,
  getReportValueDisabledReason,
  isFieldDisabled
} from 'utils/reportDisabledFields'

const useStyles = makeStyles(() => ({
  root: {
    height: '100%'
  },
  content: {
    padding: 0
  },
  image: {
    height: 48,
    width: 48
  },
  actions: {
    justifyContent: 'flex-end'
  },
  place: {
    paddingRight: 10,
    paddingLeft: 10
  }
}))

const Ranking = ({
  className = '',
  disabledFields = {},
  sellers = [],
  storeId,
  ...rest
}) => {
  const store = useSelector(state => state.store.store)

  const classes = useStyles({})
  const history = useHistory()
  const salesDisabledReason = getReportValueDisabledReason(
    disabledFields,
    'totalSold'
  )
  const salesDisabled = isFieldDisabled(salesDisabledReason)
  const goalPercentageReason = getReportValueDisabledReason(
    disabledFields,
    'goalPercent'
  )
  const goalPercentageDisabled = isFieldDisabled(goalPercentageReason)

  return (
    <Card {...rest} className={clsx(classes.root, className)}>
      <CardHeader subtitle={`${sellers.length} vendedores`} title='Ranking' />
      <Divider />
      <CardContent className={classes.content}>
        <List>
          {sellers.length > 0 ? (
            [...sellers]
              .sort((a, b) => {
                const byName = a.name.complete.localeCompare(b.name.complete)
                const bySold = salesDisabled
                  ? byName
                  : b.total.sold - a.total.sold || byName

                if (
                  store?.config.sellersSortRanking > 0 &&
                  !goalPercentageDisabled
                ) {
                  const byGoal =
                    safeDivision(b.total.sold, b.metric.acDayGoal) * 100 -
                    safeDivision(a.total.sold, a.metric.acDayGoal) * 100

                  return byGoal || bySold
                }

                return bySold
              })
              .splice(0, 4)
              .map((seller, i) => (
                <ListItem divider={i < sellers.length - 1} key={i}>
                  <Typography
                    style={{ color: '#2A3ECB' }}
                    variant='h2'
                    className={classes.place}
                  >
                    {i + 1}˚
                  </Typography>
                  <ListItemText
                    primary={
                      <>
                        <Typography
                          variant='h6'
                          style={{ display: 'inline-block' }}
                        >{`${seller.name.first} -`}</Typography>
                        <Typography
                          variant='h6'
                          style={{
                            paddingLeft: 3,
                            display: 'inline-block',
                            color: goalPercentageDisabled
                              ? 'inherit'
                              : (value =>
                                  value >= 100
                                    ? '#27ae60'
                                    : value >= 80
                                    ? '#f39c12'
                                    : '#e74c3c')(
                                  safeDivision(
                                    seller.total.sold,
                                    seller.metric.acDayGoal
                                  ) * 100
                                )
                          }}
                        >
                          {goalPercentageDisabled ? (
                            <UnavailableValue
                              message={salesDisabled
                                ? salesDisabledReason === 'FUTURE_RANGE'
                                  ? FUTURE_SALES_MESSAGE
                                  : getDisabledFieldMessage(salesDisabledReason)
                                : getDisabledFieldMessage(goalPercentageReason)}
                            />
                          ) : formatToPercentage(
                            safeDivision(
                              seller.total.sold,
                              seller.metric.acDayGoal
                            ) * 100
                          )}
                        </Typography>
                      </>
                    }
                    secondary={salesDisabled
                      ? (
                        <UnavailableValue
                          message={salesDisabledReason === 'FUTURE_RANGE'
                            ? FUTURE_SALES_MESSAGE
                            : getDisabledFieldMessage(salesDisabledReason)}
                        />
                        )
                      : `${formatPrice(seller.total.sold)} vendidos.`}
                  />
                </ListItem>
              ))
          ) : (
            <ListItem key={0}>
              <ListItemText secondary='Não há meta para esse mês.' />
            </ListItem>
          )}
        </List>
      </CardContent>
      <Divider />
      <CardActions className={classes.actions}>
        <Button
          color='primary'
          size='small'
          variant='text'
          onClick={() => history.push(`/${storeId}/report`)}
        >
          Ver relatório <ArrowRightIcon />
        </Button>
      </CardActions>
    </Card>
  )
}

Ranking.propTypes = {
  className: PropTypes.string
}

export default Ranking
